import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { currentLanguage, t } from "../../i18n";
import Header from "../../components/Header/Header";
import FooterSection from "../../components/FooterSection/FooterSection";
import AnimatedSection from "../../components/AnimatedSection/AnimatedSection";
import styled from "@emotion/styled";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";

interface Document {
  id: number;
  titleKey: string;
  url: string;
}

// Belgeler listesi - kullanıcı buraya belge linklerini ekleyecek
const documents: Document[] = [
  { id: 1, titleKey: "documents.list.doc1", url: "/documents/siteuyesiaydinlatma.pdf" },
  { id: 2, titleKey: "documents.list.doc2", url: "/documents/Privacy Policy.pdf" },
  { id: 3, titleKey: "documents.list.doc3", url: "/documents/3-Iletisim-Formu-Acik-Riza-Metni.pdf" },
  { id: 4, titleKey: "documents.list.doc4", url: "/documents/web-Cerez-Politikasi-1.pdf" },
  { id: 5, titleKey: "documents.list.doc5", url: "/documents/İlgili Kişi Başvuru Formu.pdf" },
  { id: 6, titleKey: "documents.list.doc6", url: "/documents/İletişim Formu Aydınlatma Metni.pdf" },
  { id: 7, titleKey: "documents.list.doc7", url: "/documents/Çalışan Adayı Aydınlatma Metni.pdf" },
];

const DocumentsPage: React.FC = () => {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const location = useLocation();

  const solutionsRef = useRef<HTMLDivElement>(null);
  const blogRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const missionRef = useRef<HTMLDivElement>(null);
  const pressRef = useRef<HTMLDivElement>(null);
  const testimonialRef = useRef<HTMLDivElement>(null);
  const showcaseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    const currentHash = location.hash;

    if (!currentPath.match(/^\/[a-z]{2}(\/|$)/)) {
      const newPath = `/${currentLanguage}${currentPath === "/" ? "" : currentPath}${currentHash}`;
      window.history.replaceState(null, "", newPath);
    }
  }, [location.pathname, location.hash]);

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleDocumentClick = (document: Document) => {
    const url = encodeURI(document.url);
    if (isMobile) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setSelectedDocument({ ...document, url });
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedDocument(null);
  };

  return (
    <div>
      <Header />
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 md:px-20 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 text-center px-2">{t("documents.title")}</h2>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-8 sm:mb-12 max-w-2xl mx-auto px-2">{t("documents.subtitle")}</p>
          </AnimatedSection>

          {documents.length === 0 ? (
            <AnimatedSection delay={200}>
              <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 text-center">
                <p className="text-sm sm:text-base text-gray-600">{t("documents.noDocuments")}</p>
              </div>
            </AnimatedSection>
          ) : (
            <div className="grid gap-4 sm:gap-6 md:gap-8 grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {documents.map((document, index) => (
                <AnimatedSection key={document.id} delay={index * 100}>
                  <div
                    className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer p-4 sm:p-6 border border-gray-200 hover:border-primaryPurple-light"
                    onClick={() => handleDocumentClick(document)}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primaryPurple-light bg-opacity-10 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primaryPurple-light" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2 hover:text-primaryPurple-light transition-colors break-words">
                          {t(document.titleKey)}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">{t("documents.clickToView")}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PDF Modal */}
      {selectedDocument && (
        <StyledDialog
          visible={isModalVisible}
          onHide={handleCloseModal}
          style={{
            width: "95vw",
            height: "95vh",
            maxWidth: "1200px",
            maxHeight: "800px",
          }}
          modal={true}
          closable={true}
          draggable={false}
          resizable={false}
          className="pdf-modal"
          header={t(selectedDocument.titleKey)}
        >
          <div className="w-full h-full pdf-iframe-container">
            <iframe src={selectedDocument.url} width="100%" height="100%" style={{ border: "none" }} title={t(selectedDocument.titleKey)} className="pdf-iframe" />
          </div>
        </StyledDialog>
      )}

      <FooterSection
        missionRef={missionRef}
        pressRef={pressRef}
        testimonialRef={testimonialRef}
        blogRef={blogRef}
        showcaseRef={showcaseRef}
        solutionsRef={solutionsRef}
        contactRef={contactRef}
      />
    </div>
  );
};

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    background-color: #fff;
    color: #000;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .p-dialog-header {
    background-color: #f8f9fa;
    color: #000;
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
    flex-shrink: 0;
  }

  .p-dialog-header .p-dialog-title {
    font-weight: 600;
    font-size: 1.1rem;
    word-break: break-word;
  }

  .pdf-iframe-container {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .pdf-iframe {
    width: 100%;
    height: 100%;
    min-height: 400px;
  }

  @media (max-width: 768px) {
    .p-dialog {
      margin: 0.5rem;
      width: calc(100vw - 1rem) !important;
      height: calc(100vh - 1rem) !important;
      max-width: calc(100vw - 1rem) !important;
      max-height: calc(100vh - 1rem) !important;
    }

    .p-dialog-content {
      padding: 0;
      height: 100%;
    }

    .p-dialog-header {
      padding: 0.75rem 1rem;
    }

    .p-dialog-header .p-dialog-title {
      font-size: 0.9rem;
      line-height: 1.3;
    }

    .pdf-iframe {
      min-height: 300px;
    }
  }

  @media (max-width: 480px) {
    .p-dialog {
      margin: 0.25rem;
      width: calc(100vw - 0.5rem) !important;
      height: calc(100vh - 0.5rem) !important;
      max-width: calc(100vw - 0.5rem) !important;
      max-height: calc(100vh - 0.5rem) !important;
    }

    .p-dialog-content {
      padding: 0;
    }

    .p-dialog-header {
      padding: 0.5rem 0.75rem;
    }

    .p-dialog-header .p-dialog-title {
      font-size: 0.85rem;
    }

    .pdf-iframe {
      min-height: 250px;
    }
  }
`;

export default DocumentsPage;
